import React, { useEffect, useState } from "react";
import {Grommet} from "grommet";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import theme from "../utils/theme";
import { useRouter } from 'next/router';

const ThemeProvider = ({ children, initialTheme }) => {
    return (
        <NextThemesProvider attribute="class" defaultTheme="light" enableSystem={true} themes={['light', 'dark']}>
            <GrommetThemeProvider initialTheme={initialTheme}>
                {children}
            </GrommetThemeProvider>
        </NextThemesProvider>
    );
};

const GrommetThemeProvider = ({ children, initialTheme }) => {
    const { resolvedTheme } = useTheme();
    const { asPath } = useRouter();

    /*
        Persist the RESOLVED mode, and only ever 'light' or 'dark'. The server reads this
        cookie to pick its render mode and cannot evaluate 'system' itself, so storing the
        preference verbatim left it guessing light while the blocking script in _document.js
        resolved the same cookie to dark via matchMedia.

        The guard matters because resolvedTheme really can be the literal 'system' here: on
        the first client render next-themes seeds systemTheme from the same storage key as
        the preference, and only the matchMedia effect corrects it to light/dark. Writing
        that through would downgrade the stored value.
    */
    useEffect(() => {
        if (resolvedTheme === 'light' || resolvedTheme === 'dark') {
            document.cookie = `theme=${resolvedTheme};path=/;max-age=31536000;SameSite=Lax`;
        }
    }, [resolvedTheme]);

    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    /*
        The server render and the browser's first render must agree. The server takes the
        mode from the initialTheme prop injected in _document.js, and the browser takes that
        same value back from __INITIAL_THEME__, which _document.js bakes into the page. The
        browser must not re-read the cookie: a page optimised at build time was rendered
        light whatever the cookie says.

        resolvedTheme deliberately does not participate until after mount. next-themes 0.2.1
        seeds its state synchronously inside the useState initialisers, so resolvedTheme is
        already truthy on the browser's first render and would silently override whatever the
        server chose. For an auto/system user it is also the literal 'system' at that point,
        which normalises to light and hands a dark-OS user light markup over a dark server
        render. Deferring to `mounted` keeps the two renders identical and lets next-themes
        correct the mode once, afterwards.
    */
    const serverTheme = initialTheme || (typeof window !== 'undefined' ? window.__INITIAL_THEME__ : undefined);
    const settledTheme = resolvedTheme === 'dark' || resolvedTheme === 'light' ? resolvedTheme : undefined;
    let mode = ((mounted && settledTheme) || serverTheme || 'light') === 'dark' ? 'dark' : 'light';

    /*
        Always light: signup, /p/<token>, /d/<token> and /hi/<slug>. Every one of these
        is shown to someone who is not our user and has no theme preference with us -
        a customer of a customer following a link - so they render the merchant's
        branding on a known background rather than inheriting a dark mode nobody here
        chose. The single-character class would match /d and /p only; hi is spelled out
        so /history-style routes cannot slip in.
    */
    mode = /^\/(signup($|\/)|(hi|[pd])\/[^/]+).*/.test(asPath) ? 'light' : mode;

    return (
        <Grommet themeMode={mode} theme={theme} full className="grommet-wrapper">
            {children}
        </Grommet>
    );
};

export default ThemeProvider;
